// CASE-01 ordinary ownership-conflict resolution (`POST /cases/{case_id}/ownership-conflicts/
// {conflict_id}/resolve`) — dev-only, default-off remote+local repair. Deliberately distinct from
// the triage pair (dismiss/snooze/reopen, visibility only) and from the generic status control: this
// is the one action that actually closes a `menu_shell_ownership` case, so it renders as its own
// bordered, differently-colored panel rather than another `courier-btn` in the triage row.
import { useState } from "react";
import { AlertTriangle, Check, HelpCircle, Scale } from "lucide-react";

import { ApiError, apiPost } from "../api";
import { isOwnershipStaleError, newRequestId, ownershipOutcomeKind } from "../cases";
import type { CaseDetail, MenuShellOwnershipEvidence, OwnershipResolutionOutcome } from "../types";

type Outcome =
  | { kind: "resolved"; result: OwnershipResolutionOutcome }
  | { kind: "refused"; reason: string }
  | { kind: "unknown"; reason: string | null }
  | { kind: "stale"; code: string }
  | { kind: "error"; message: string };

function reasonLabel(code: string): string {
  return code.replace(/_/g, " ");
}

export default function OwnershipResolutionPanel({
  caseId,
  detail,
  canAct,
  onResolved,
  onReloadRequested,
}: {
  caseId: string;
  detail: CaseDetail;
  canAct: boolean;
  onResolved: () => void;
  onReloadRequested: () => void;
}) {
  const [reason, setReason] = useState("");
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const capabilityEnabled = detail.capabilities?.ownership_resolution_enabled === true;
  const evidence = detail.source_evidence;
  const current = evidence?.current_source ?? null;

  // Only ever render for an open `menu_shell_ownership` conflict whose remote-fact repair is
  // actually rolled on for this shop. A resolved/superseded/other-kind case shows nothing here —
  // the ordinary status/messages panel already reflects its outcome.
  if (!capabilityEnabled || !current || current.kind !== "menu_shell_ownership") return null;

  const conflictEvidence = current.evidence as unknown as MenuShellOwnershipEvidence;
  const requestingVendorId = current.vendor_id;
  const conflictId = current.source_id;
  const expectedFingerprint = current.fingerprint;
  const expectedVersion = detail.case.version;

  const onReasonChange = (value: string) => {
    setReason(value);
    // A different reason is a different attempt — a fresh client_request_id, never reused.
    if (attemptId !== null) setAttemptId(null);
  };

  const submit = async () => {
    if (reason.trim().length < 10) {
      setOutcome({ kind: "error", message: "Reason must be at least 10 characters" });
      return;
    }
    const requestId = attemptId ?? newRequestId();
    setAttemptId(requestId);
    setBusy(true);
    try {
      const body = await apiPost<{ resolution: OwnershipResolutionOutcome }>(
        `/cases/${caseId}/ownership-conflicts/${conflictId}/resolve`,
        {
          client_request_id: requestId,
          expected_case_version: expectedVersion,
          expected_source_fingerprint: expectedFingerprint,
          reason: reason.trim(),
        },
      );
      const result = body.resolution;
      if (result.resolved) {
        setOutcome({ kind: "resolved", result });
        setAttemptId(null);
        setReason("");
        onResolved();
        return;
      }
      const bucket = ownershipOutcomeKind(result.reason);
      setOutcome(
        bucket === "refused"
          ? { kind: "refused", reason: result.reason ?? "unknown" }
          : { kind: "unknown", reason: result.reason },
      );
      // A refused or unknown 200 outcome is a real answer for THIS attempt; the next click is a
      // deliberate new attempt (e.g. after fixing the underlying fact), so mint a fresh id. An
      // "unknown" remote observation is safe to retry with the SAME attempt id (never auto —
      // the operator clicks again), so keep it live for that retry.
      if (bucket === "refused") setAttemptId(null);
    } catch (e) {
      if (e instanceof ApiError) {
        if (isOwnershipStaleError(e.status, e.message)) {
          setOutcome({ kind: "stale", code: e.message });
          setAttemptId(null);
        } else if (e.status === 404) {
          setOutcome({ kind: "stale", code: e.message });
          setAttemptId(null);
        } else {
          // Any other transport/server failure is an uncertain remote outcome, not a refusal —
          // keep the same attempt id so a manual retry is idempotent on the server.
          setOutcome({ kind: "unknown", reason: e.message });
        }
      } else {
        setOutcome({ kind: "unknown", reason: "request failed" });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ownership-resolve-panel">
      <p className="cap-note">
        <Scale size={11} /> Ownership conflict — the requesting vendor's live menu shell disagrees
        with the catalog's current owner. Resolving reassigns catalog ownership only after a
        Shopify-first read (and, if needed, an exact-readback write) confirms the requesting
        vendor as the true remote owner.
      </p>

      <dl className="ownership-evidence">
        <div>
          <dt>Product</dt>
          <dd>#{conflictEvidence.shopify_product_id}</dd>
        </div>
        <div>
          <dt>Requesting vendor (live menu shell)</dt>
          <dd>{requestingVendorId.slice(0, 8)}</dd>
        </div>
        <div>
          <dt>Existing catalog owner</dt>
          <dd>{conflictEvidence.existing_vendor_id.slice(0, 8)}</dd>
        </div>
        <div>
          <dt>Shell kind</dt>
          <dd>{conflictEvidence.shell_kind}</dd>
        </div>
        <div>
          <dt>Conflict reason</dt>
          <dd>{reasonLabel(current.reason)}</dd>
        </div>
        <div>
          <dt>Last seen</dt>
          <dd>{new Date(conflictEvidence.last_seen_at).toLocaleString()}</dd>
        </div>
      </dl>

      {canAct ? (
        <>
          <textarea
            className="recover-textarea"
            placeholder="Reason for resolving this ownership conflict (min 10 characters)"
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
          />
          <div className="recover-actions">
            <button
              className="ownership-resolve-btn"
              disabled={busy || reason.trim().length < 10}
              onClick={() => void submit()}
            >
              <Scale size={12} /> {busy ? "Resolving…" : "Resolve ownership"}
            </button>
          </div>
        </>
      ) : (
        <p className="cap-note">Owner/admin can resolve this ownership conflict.</p>
      )}

      {outcome?.kind === "resolved" && (
        <p className="ownership-outcome ownership-outcome-resolved">
          <Check size={12} /> Resolved — catalog ownership reassigned to the requesting vendor
          {outcome.result.remote_effect_applied ? " (Shopify owner metafield written and read back)" : " (remote owner already agreed)"}.
        </p>
      )}
      {outcome?.kind === "refused" && (
        <p className="ownership-outcome ownership-outcome-refused">
          <AlertTriangle size={12} /> Refused — {reasonLabel(outcome.reason)}. This conflict was
          not resolved; the underlying fact needs to change before retrying.
        </p>
      )}
      {outcome?.kind === "unknown" && (
        <p className="ownership-outcome ownership-outcome-unknown">
          <HelpCircle size={12} /> Unknown outcome{outcome.reason ? ` — ${reasonLabel(outcome.reason)}` : ""}.
          The remote effect is unconfirmed; this was NOT auto-retried. Verify manually or click
          Resolve again to retry the same attempt.
        </p>
      )}
      {outcome?.kind === "stale" && (
        <p className="ownership-outcome ownership-outcome-stale">
          <AlertTriangle size={12} /> {reasonLabel(outcome.code)} — this case or conflict changed
          since it was loaded.{" "}
          <button className="courier-btn" onClick={onReloadRequested}>
            Reload
          </button>
        </p>
      )}
      {outcome?.kind === "error" && <p className="dlq-error">{outcome.message}</p>}
    </div>
  );
}
